/**
 * Unanswered-speech watcher (ADR 0036).
 *
 * "Stale" means the client is speaking and neither an interim nor a final has
 * arrived. Silence does not count, and a wall-clock gap while nobody is talking
 * does not count. Finals are held server-side until the sentence ends, so
 * interim text counts as heard too.
 *
 * `transcriptStaleSec` is accumulated speaking time since the last text. The
 * capture card turns amber at `UNANSWERED_SPEECH_STALE_SEC`.
 *
 * Also stamps `lastSegmentAtRef` when text arrives so the recording health
 * check sees the same "we heard words" signal.
 */
import { useEffect, useRef, useState } from "react"
import type { TRecordingMode } from "../interfaces"

/** Same cutoff as the vad heartbeat in useStartRecording (`VAD_SILENCE_THRESHOLD`). */
export const CLIENT_SPEAKING_VOLUME = 0.04

const TICK_MS = 1_000

export function isClientSpeaking(volume: number): boolean {
  return volume >= CLIENT_SPEAKING_VOLUME
}

interface IProps {
  mode: TRecordingMode
  isActive: boolean
  liveSegmentCount: number
  /** Latest interim line. A non-empty change resets the unanswered clock. */
  interimText: string
  /** True only while the client would send `{type:"vad", speaking:true}`. */
  speaking: boolean
  lastSegmentAtRef: React.MutableRefObject<number>
}

interface IUseTranscriptStaleWatchResult {
  transcriptStaleSec: number
}

export function useTranscriptStaleWatch({
  mode,
  isActive,
  liveSegmentCount,
  interimText,
  speaking,
  lastSegmentAtRef,
}: IProps): IUseTranscriptStaleWatchResult {
  const [transcriptStaleSec, setTranscriptStaleSec] = useState(0)
  const speakingMsRef = useRef(0)
  const speakingRef = useRef(speaking)
  speakingRef.current = speaking
  const heardRef = useRef({ segments: liveSegmentCount, interim: interimText })

  useEffect(() => {
    const prev = heardRef.current
    const segmentAdvanced = liveSegmentCount !== prev.segments
    const interimAdvanced = interimText.length > 0 && interimText !== prev.interim
    heardRef.current = { segments: liveSegmentCount, interim: interimText }
    if (!segmentAdvanced && !interimAdvanced) return
    speakingMsRef.current = 0
    setTranscriptStaleSec(0)
    lastSegmentAtRef.current = Date.now()
  }, [liveSegmentCount, interimText, lastSegmentAtRef])

  useEffect(() => {
    if (mode !== "streaming" || !isActive) {
      speakingMsRef.current = 0
      setTranscriptStaleSec(0)
      return
    }
    let last = Date.now()
    const interval = setInterval(() => {
      const now = Date.now()
      const delta = now - last
      last = now
      if (!speakingRef.current) return
      speakingMsRef.current += delta
      setTranscriptStaleSec(Math.floor(speakingMsRef.current / 1000))
    }, TICK_MS)
    return () => clearInterval(interval)
  }, [mode, isActive])

  return { transcriptStaleSec }
}
